import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Dropdown, List, Menu, Spin } from 'antd';
import { useCallback, useMemo } from 'react';
import Hotkeys from 'react-hot-keys';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import MoreSvg from '../../../../assets/icons/MoreSvg';
import { RecycleSvg } from '../../../../assets/icons/RecycleSvg';
import theme from '../../../../assets/theme';
import {
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useRunTestcases from '../../../../hooks/useRunTestcases';
import useTestcase from '../../../../hooks/useTestcase';
import useTestcases from '../../../../hooks/useTestcases';
import useUpdateStoredPromptInputData from '../../../../hooks/useUpdateStoredPromptInputData';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import {
  ById,
  PromptInput,
  PromptInputData,
  PromptSingleTestCaseResult,
} from '../../../../services/backendService/types';
import { reconnectPromptDependantElements } from '../../../../utils/aiGeneratorUtils';
import { getIndexToColor, typeToActivityMap } from '../../../../utils/creator';
import { getRoundCategory } from '../../../../utils/roundUtils';
import { zIndexes } from '../../../../utils/zIndexes';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import ActivityIndicator from '../../../SlideComponents/ActivityIndicator';
import { Slide } from '../../../SlideComponents/slide.types';
import { slideTypeToIcon } from '../../../SlideComponents/slideUtil';
import SlideWrapperViwer from '../../../SlideComponents/SlideWrapperViewer';

const TestCasesResultTab = () => {
  const testcaseResults = useDesignerUIContext(c => c.state.testcaseResults);
  const dispatch = useDesignerUIContext(c => c.dispatch);
  const dataDispatch = useDesignerDataContext(c => c.dispatch);
  const selectedTestcaseResult = useDesignerUIContext(
    c => c.state.selectedTestcaseResult,
  );
  const localTemplate = useDesignerDataContext(c => c.state.localTemplate);

  const currentlyEditableSlides = localTemplate?.content.slides || [];

  const presentationId = useDesignerDataContext(c => c.state.localTemplate?.id);
  const { forceUpdateCacheData } = useUpdateStoredPromptInputData(presentationId);

  const { testcases } = useTestcases({
    presentationId,
  });

  // The list of testcase results to display
  const listData:
    | {
        id: string;
        inputs: PromptInput[];
        slides: Slide[];
        allTestcasePromptOutputs?: PromptSingleTestCaseResult;
        lessonAttachments?: LessonAttachment[];
        isSuggestion?: boolean;
        isSampleLesson?: boolean;
      }[]
    | undefined = useMemo(() => {
    const promptInputs = localTemplate?.aiContent?.promptInputs;
    if (!testcaseResults) return [];

    return testcaseResults.map(r => {
      const inputs: PromptInput[] = r.inputs.map(i => {
        const promptInput = promptInputs?.find(p => p.id === i.id);
        if (!promptInput) return i as PromptInput;
        return {
          ...i,
          label: promptInput.label,
        } as PromptInput;
      });

      const findTestcase = testcases?.find(t => t.id === r.id);

      return {
        id: r.id,
        inputs: inputs,
        slides: r.slides,
        allTestcasePromptOutputs: r.allTestcasePromptOutputs,
        lessonAttachments: r.lessonAttachments,
        isSuggestion: findTestcase?.isSuggestion,
        isSampleLesson: findTestcase?.isSampleLesson,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTemplate?.aiContent?.promptInputs, testcaseResults, testcases]);

  const slideList = useMemo(() => {
    if (!selectedTestcaseResult) return [];
    if (!selectedTestcaseResult.slides) return [];
    return selectedTestcaseResult.slides.filter(
      s =>
        getRoundCategory(s.type) === 'response' || getRoundCategory(s.type) === 'slide',
    );
  }, [selectedTestcaseResult]);

  // finds the slides to be displayed in the top slide viewer
  const currentTestcaseSlide = useMemo(() => {
    if (!slideList) return undefined;
    const currentSlide = slideList.find(s => s.id === localTemplate?.currentRound);
    if (currentSlide) return currentSlide;
  }, [localTemplate?.currentRound, slideList]);

  const { isRunningTestcases, runTestcases } = useRunTestcases();
  const handleSlideClicked = (slideId: string) => {
    dataDispatch({
      type: TemplateStateActions.changeSelectedSlide,
      slideId: slideId,
    });
  };

  const handleTestcaseResultSelected = (result: {
    id: string;
    inputs: PromptInputData[];
    lessonAttachments?: LessonAttachment[];
    slides: Slide[];
  }) => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTestcaseResult,
      payload: result,
    });
  };

  const handleArrowKeyPress = (direction: 'up' | 'down') => {
    if (!testcaseResults) return;
    if (!selectedTestcaseResult) return;
    const currentIndex = testcaseResults.findIndex(
      r => r.id === selectedTestcaseResult.id,
    );
    if (direction === 'up') {
      if (currentIndex === 0) {
        handleTestcaseResultSelected(testcaseResults[testcaseResults.length - 1]);
        return;
      }
      handleTestcaseResultSelected(testcaseResults[currentIndex - 1]);
    } else {
      if (currentIndex === testcaseResults.length - 1) {
        handleTestcaseResultSelected(testcaseResults[0]);
        return;
      }
      handleTestcaseResultSelected(testcaseResults[currentIndex + 1]);
    }
  };

  const { editTestcase } = useTestcase({
    presentationId,
  });

  const handleUseAsSugestion = (
    shouldUseAsSuggestion: boolean,
    testcaseId: string,
    inputs: PromptInputData[],
    isSampleLesson: boolean,
  ) => {
    if (!selectedTestcaseResult) return;
    editTestcase({
      inputData: inputs.map(input => {
        return {
          id: input.id,
          type: input.type,
          value: input.value as string | undefined,
        };
      }),
      isSuggestion: shouldUseAsSuggestion,
      isSampleLesson: isSampleLesson,
      id: testcaseId,
    });
  };

  const handleUseAsSampleLesson = (
    isSuggestion: boolean,
    testcaseId: string,
    inputs: PromptInputData[],
    isSampleLesson: boolean,
  ) => {
    if (!selectedTestcaseResult) return;
    editTestcase({
      inputData: inputs.map(input => {
        return {
          id: input.id,
          type: input.type,
          value: input.value as string | undefined,
        };
      }),
      isSuggestion: isSuggestion,
      isSampleLesson: isSampleLesson,
      id: testcaseId,
    });
  };

  const handleRegenerate = useCallback(
    (testcaseIds?: string[]) => {
      if (!presentationId) return;
      if (!testcases) return;

      runTestcases({
        presentationId,
        onSuccessFunction: (
          results: ById<{
            slides: Slide[];
            allTestcasePromptOutputs?: PromptSingleTestCaseResult;
            lessonAttachments?: LessonAttachment[];
            error?: string;
          }>,
        ) => {
          const testResultPayLoad: {
            id: string;
            inputs: PromptInputData[];
            slides: Slide[];
            allTestcasePromptOutputs?: PromptSingleTestCaseResult;
            lessonAttachments?: LessonAttachment[];
            isSuggestion: boolean;
            error?: string;
          }[] = Object.entries(results).reduce((acc, [key, value]) => {
            // merges the testresults and the testcases into a single payload
            const testcase = testcases.find(r => r.id === key);
            if (!testcase) return acc;
            const newTestcaseResult = {
              id: testcase.id,
              inputs: testcase.inputs,
              slides: value.slides,
              allTestcasePromptOutputs: value.allTestcasePromptOutputs,
              isSuggestion: testcase.isSuggestion,
              lessonAttachments: value.lessonAttachments,
              error: value.error,
            };
            return [...acc, newTestcaseResult];
          }, [] as { id: string; inputs: PromptInputData[]; slides: Slide[]; isSuggestion: boolean; lessonAttachments?: LessonAttachment[] }[]);
          if (testResultPayLoad.length === 0) return;
          dispatch({
            type: DesignerUIStateActions.setTestcaseResults,
            payload: testResultPayLoad,
          });
          dispatch({
            type: DesignerUIStateActions.setSelectedTestcaseResult,
            payload: testResultPayLoad[0],
          });
        },
        testcaseIds: testcaseIds ? testcaseIds : testcases.map(r => r.id),
      });
    },
    [dispatch, presentationId, runTestcases, testcases],
  );

  if (!testcaseResults) return <Container>Missing testcase results</Container>;
  return (
    <FullWidthContainer>
      <Hotkeys keyName={'up'} onKeyUp={() => handleArrowKeyPress('up')} />
      <Hotkeys keyName={'down'} onKeyUp={() => handleArrowKeyPress('down')} />
      <TestCaseResultContainer>
        <TestCaseResultLeftColumn>
          <div>
            <span
              style={{ display: 'flex', cursor: 'pointer' }}
              onClick={() =>
                dispatch({
                  type: DesignerUIStateActions.setSelectedTab,
                  payload: {
                    tab: 'testCases',
                  },
                })
              }
            >
              {' '}
              <IconWrapper>
                <ArrowLeftSvg />
              </IconWrapper>
              Back to testcases
            </span>
            <h1>Test case result</h1>

            <Button
              icon={<RecycleSvg />}
              loading={isRunningTestcases}
              type="ghost"
              onClick={() => handleRegenerate(undefined)}
            >
              {isRunningTestcases ? 'Re-generating' : 'Re-generate all testcases'}
            </Button>
          </div>
          <ListContainer>
            {listData?.map((item, i) => (
              <TestCaseResultListItem
                key={item.id}
                $selected={item.id === selectedTestcaseResult?.id}
                onClick={() => handleTestcaseResultSelected(item)}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    marginRight: '10px',
                  }}
                >
                  {i + 1}
                </span>
                <TestCaseResultItemContent testcase={item} />
                <span
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '5px',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          onClick={() => {
                            handleUseAsSugestion(
                              !item.isSuggestion,
                              item.id,
                              item.inputs,
                              !!item.isSampleLesson,
                            );
                          }}
                          className="align-center"
                        >
                          <span style={{ display: 'flex', gap: '10px' }}>
                            Use as suggestion?{' '}
                            <Checkbox
                              checked={item.isSuggestion}
                              onChange={shouldUseAsSuggestion => {
                                handleUseAsSugestion(
                                  shouldUseAsSuggestion.target.checked,
                                  item.id,
                                  item.inputs,
                                  !!item.isSampleLesson,
                                );
                              }}
                            />
                          </span>
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => {
                            handleUseAsSampleLesson(
                              !!item.isSuggestion,
                              item.id,
                              item.inputs,
                              !item.isSampleLesson,
                            );
                          }}
                          className="align-center"
                        >
                          <span style={{ display: 'flex', gap: '10px' }}>
                            Use as sample lesson
                            <Checkbox
                              checked={item.isSampleLesson}
                              onChange={shouldUseAsSampleLesson => {
                                handleUseAsSampleLesson(
                                  !!item.isSuggestion,
                                  item.id,
                                  item.inputs,
                                  shouldUseAsSampleLesson.target.checked,
                                );
                              }}
                            />
                          </span>
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => {
                            if (!selectedTestcaseResult?.allTestcasePromptOutputs) return;
                            forceUpdateCacheData(
                              selectedTestcaseResult.allTestcasePromptOutputs,
                            );
                            if (!localTemplate) return;

                            const {
                              slides: newSlides,
                            } = reconnectPromptDependantElements({
                              promptResultData:
                                selectedTestcaseResult?.allTestcasePromptOutputs,
                              slides: currentlyEditableSlides,
                            });

                            dataDispatch({
                              type: TemplateStateActions.allSlidesChanged,
                              slides: newSlides,
                              selectedSlideId: currentlyEditableSlides[0].id,
                            });
                            dispatch({
                              type: DesignerUIStateActions.setSelectedTab,
                              payload: {
                                tab: 'prompts',
                              },
                            });
                          }}
                        >
                          Set as editable lesson
                        </Menu.Item>
                        <Menu.Item
                          style={{ display: 'flex', flexDirection: 'row-reverse' }}
                          onClick={() => {
                            handleRegenerate([item.id]);
                          }}
                          className="align-center"
                          icon={
                            <IconWrapper
                              style={{ marginLeft: '10px', marginRight: '0px' }}
                            >
                              {isRunningTestcases ? (
                                <Spin
                                  indicator={
                                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                                  }
                                />
                              ) : (
                                <RecycleSvg />
                              )}
                            </IconWrapper>
                          }
                        >
                          Re-run this testcase
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <IconWrapper color="#A5A5A5" height="24px" width="24px">
                      <MoreSvg />
                    </IconWrapper>
                  </Dropdown>
                </span>
              </TestCaseResultListItem>
            ))}
          </ListContainer>
        </TestCaseResultLeftColumn>
        <TestCaseResultRightColumn>
          {selectedTestcaseResult?.error && (
            <div>
              <h1>Error generating by this test case</h1>
              <p>{selectedTestcaseResult?.error}</p>
            </div>
          )}
          <TestCaseResultRightColumnTop>
            {currentTestcaseSlide && (
              <SlideWrapperViwer
                aspect={{ height: 1080, width: 1920 }}
                slide={currentTestcaseSlide}
                backgroundMedia={currentTestcaseSlide?.backgroundMedia}
                resultView={false}
                showStaticPoll={true}
                hideLogo={false}
                isDiscoverTemplate={false}
                isOwner={true}
                lessonAttachments={selectedTestcaseResult?.lessonAttachments}
              />
            )}
          </TestCaseResultRightColumnTop>
          <TestCaseResultRightColumnBottom>
            {slideList.map((slide, i) => (
              <div key={slide.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                  {i + 1} {typeToActivityMap(slide.type)?.title}
                </span>
                <TestCaseResultSlideWrapperViwer
                  $selected={slide.id === currentTestcaseSlide?.id}
                  onClick={() => {
                    handleSlideClicked(slide.id);
                  }}
                >
                  <SlideWrapperViwer
                    aspect={{ height: 1080, width: 1920 }}
                    slide={slide}
                    backgroundMedia={slide.backgroundMedia}
                    resultView={false}
                    showStaticPoll={true}
                    hideLogo={false}
                    isDiscoverTemplate={false}
                    lessonAttachments={selectedTestcaseResult?.lessonAttachments}
                    isOwner={true}
                  >
                    <SlideViewerOverlay>
                      <div
                        style={{
                          height: '100%',
                          width: '100%',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          zIndex: zIndexes('ACTIVITY_OVERLAY_ITEM'),
                        }}
                      >
                        {slide.type !== 'slide' && (
                          <>
                            <ActivityOverlayItem>
                              <IconWrapper color="white" height={'10px'} width={'10px'}>
                                {slideTypeToIcon(slide.type)}
                              </IconWrapper>
                            </ActivityOverlayItem>
                          </>
                        )}
                        {slide.type !== 'slide' && (
                          <>
                            <ActivityIndicator isSlideList={true} slide={slide} />
                          </>
                        )}
                      </div>
                    </SlideViewerOverlay>
                  </SlideWrapperViwer>
                </TestCaseResultSlideWrapperViwer>
              </div>
            ))}
          </TestCaseResultRightColumnBottom>
        </TestCaseResultRightColumn>
      </TestCaseResultContainer>
    </FullWidthContainer>
  );
};

const ListContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const TestCaseResultItemContent = ({
  testcase,
}: {
  testcase: {
    id: string;
    inputs: PromptInput[];
    slides: Slide[];
  };
}) => {
  return (
    <TestCaseResultItemContentContainer>
      {testcase.inputs.length === 0 && (
        <>
          <TestCaseResultItemContentLabel>
            <span style={{ opacity: 0.5, marginLeft: 5 }}>No inputs</span>
          </TestCaseResultItemContentLabel>
        </>
      )}
      {testcase.inputs.map((input, i) => (
        <TestCaseResultItemContentInput key={input.id}>
          <TestCaseResultItemContentLabel>
            <span style={{ opacity: 0.5 }}>{input.label ?? ''}</span>
          </TestCaseResultItemContentLabel>
          <TestCaseResultItemContentValue>
            <span
              style={{
                borderLeft: `3px solid ${getIndexToColor(i)}`,
                paddingLeft: '5px',
              }}
            >
              {input.value
                ? input.value.toString().length > 30
                  ? `${input.value.toString().slice(0, 30)}...`
                  : input.value.toString()
                : ''}
            </span>
          </TestCaseResultItemContentValue>
        </TestCaseResultItemContentInput>
      ))}
    </TestCaseResultItemContentContainer>
  );
};
const FullWidthContainer = styled.div`
  height: 100%;
  width: 100%;
  background-size: cover;
  display: grid;
  padding: 20px;
  grid-template-columns: 1fr;
  flex-wrap: wrap;
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
`;
const TestCaseResultListItem = styled(List.Item)<{ $selected: boolean }>`
  position: relative;
  cursor: pointer;

  border-radius: 10px;
  height: fit-content;
  overflow: auto;
  border-bottom: none !important;

  &:hover {
    background-color: ${theme.colors.greyLight};
  }
  ${({ $selected }) =>
    $selected &&
    `
  background-color: ${theme.colors.greyLight};
  `};
`;
const Container = styled.div`
  padding: 10px;
`;

const TestCaseResultContainer = styled.div`
  display: grid;
  grid-template-columns: 400px auto;
  gap: 10px;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const TestCaseResultLeftColumn = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-rows: 125px auto;
  li {
    margin: 10px 0;
  }

  .ant-list-items {
    overflow-y: auto;
    height: 100%;
  }
  .ant-spin-container,
  .ant-spin-nested-loading {
    height: 100%;
  }
`;

const TestCaseResultRightColumn = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 150px;
  max-height: calc(100vh - 115px);
`;

const TestCaseResultRightColumnTop = styled.div`
  width: 100%;
  height: calc(100% - 10px);
`;
const TestCaseResultRightColumnBottom = styled.div`
  height: 100%;
  padding: 20px;
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  overflow-x: scroll;
  gap: 20px;
`;
const TestCaseResultSlideWrapperViwer = styled.div<{ $selected: boolean }>`
  border-radius: 10px;
  width: 170px;
  height: 100%;
  cursor: pointer;
  overflow: hidden;
  background-color: ${({ $selected }) =>
    $selected ? `${theme.colors.blue}` : theme.colors.black};
  outline: ${({ $selected }) =>
    $selected ? `2px solid ${theme.colors.blue}` : `2px solid ${theme.colors.black}`};
`;

const SlideViewerOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: transparent;

  &:hover {
    background-color: ${theme.colors.lessTranslucentWhite};
  }
`;
const ActivityOverlayItem = styled.div`
  background-color: rgba(122, 122, 122, 0.76);
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px 0 4px 0;
  padding: 4px;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
`;
const TestCaseResultItemContentContainer = styled.div`
  padding: 0;
  display: grid;
  height: 100%;
  max-height: 150px;
  overflow-y: auto;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  justify-content: flex-start;
  align-content: flex-start;
`;
const TestCaseResultItemContentInput = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
`;
const TestCaseResultItemContentValue = styled.div``;
const TestCaseResultItemContentLabel = styled.div``;

export default TestCasesResultTab;
